import type { CSSProperties } from 'react';
import { stringifyEntityRef, parseEntityRef } from '@backstage/catalog-model';
import { InfoCard, Link, Progress } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import { useComponentPage } from './ComponentPageContext';
import type { RelatedEntity, RelationVia } from './relations';
import { STATUS_COLOUR, STATUS_LABEL } from './serviceStatus';
import { visuallyHidden } from '../../visuallyHidden';

// Fixed geometry, so the edges are computed from counts rather than measured
// from the DOM: every side label is LABEL_H tall and never wraps, every node is
// NODE_H tall, one STEP apart. A label that wrapped would push its nodes down
// and away from the edges drawn for them.
const LABEL_H = 18;
const LABEL_GAP = 8;
const NODE_H = 60;
const GAP = 12;
const STEP = NODE_H + GAP;
/** Wide enough for the longest edge label, "consumes API". */
const GUTTER = 88;
/** More than this per side and the rest is a link to the Dependencies tab. */
const MAX_PER_SIDE = 6;

// Read left to right, as the graph is: "Conductor depends on AI Gateway",
// "AI Gateway depends on revenexx ID". So both sides share the words.
const RELATION_LABEL: Record<RelationVia, string> = {
  dependsOn: 'depends on',
  consumesApi: 'consumes API',
  dependencyOf: 'depends on',
  callsItsApi: 'consumes API',
};

/** Both at once ("depends on · consumes API") would not fit the gutter. */
const relationOf = (e: RelatedEntity) => {
  const labels = [...new Set(e.via.map(v => RELATION_LABEL[v]))];
  return labels.length > 1 ? 'depends on + API' : (labels[0] ?? '');
};

const useStyles = makeStyles(theme => ({
  graph: {
    display: 'grid',
    gridTemplateColumns: `minmax(0, 1fr) ${GUTTER}px minmax(0, 1fr) ${GUTTER}px minmax(0, 1fr)`,
    alignItems: 'start',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      rowGap: theme.spacing(2),
      '& $gutter': { display: 'none' },
    },
  },
  // Each stack is centred on the tallest one by an offset the edges were
  // computed with; stacked on a phone there are no edges, and no offset.
  side: {
    display: 'flex',
    flexDirection: 'column',
    gap: GAP,
    minWidth: 0,
    paddingTop: 'var(--stack-offset, 0px)',
    [theme.breakpoints.down('xs')]: { paddingTop: 0 },
  },
  sideLabel: {
    height: LABEL_H,
    lineHeight: `${LABEL_H}px`,
    marginBottom: LABEL_GAP,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'var(--ui-text-muted)',
  },
  gutter: { overflow: 'visible', marginTop: LABEL_H + LABEL_GAP },
  // Drawn over every line, with a halo in the card's own colour, so a line
  // from another node never runs through a label.
  edgeLabel: {
    fontSize: 10,
    fill: 'var(--ui-text-muted)',
    paintOrder: 'stroke',
    stroke: 'var(--ui-bg-elevated)',
    strokeWidth: 4,
    strokeLinejoin: 'round',
  },
  node: {
    position: 'relative',
    height: NODE_H,
    boxSizing: 'border-box',
    border: '1px solid var(--ui-node-border)',
    background: 'var(--ui-node-bg)',
    borderRadius: 8,
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
      borderColor: 'var(--ui-node-border-hover)',
    },
  },
  unresolved: {
    border: '1px dashed var(--ui-warn-border)',
    background: 'var(--ui-warn-bg)',
  },
  centre: {
    background: 'var(--ui-node-self-bg)',
    color: 'var(--ui-node-self-text)',
    border: '1px solid var(--ui-node-self-bg)',
  },
  statusDot: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 9,
    height: 9,
    borderRadius: '50%',
    // A ring in the node's own colour keeps the dot apart from the text.
    boxShadow: '0 0 0 2px var(--ui-node-self-bg)',
  },
  caption: {
    fontFamily: 'var(--font-mono)',
    // Small enough for "component · unresolved" in a node of a two-column
    // card; narrower still, it ends in an ellipsis and the title says it all.
    fontSize: '0.64rem',
    lineHeight: 1.3,
    opacity: 0.75,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // A side node's caption is a row, so what matters most is what stays: the
  // kind gives way before "unresolved" does.
  nodeCaption: { display: 'flex', minWidth: 0 },
  kind: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' },
  keep: { flexShrink: 0 },
  // Two lines before it is cut: component names are long and hyphenated
  // ("integrations-node-…"), and the end of one is what tells it apart.
  name: {
    fontWeight: 600,
    lineHeight: 1.25,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    overflowWrap: 'anywhere',
  },
  // On the edge where there is room for it; in the node once the graph is
  // stacked and the edges are gone. Screen readers get it either way.
  relation: {
    [theme.breakpoints.up('sm')]: visuallyHidden,
  },
  more: { fontSize: '0.8rem' },
  // Stacked, the centre has no label beside it to line up with.
  placeholder: { [theme.breakpoints.down('xs')]: { display: 'none' } },
}));

const heightFor = (count: number) => Math.max(count, 1) * STEP - GAP;

/** How far a stack of `count` sits below the top of one `height` tall. */
const offsetOf = (count: number, height: number) =>
  (height - heightFor(count)) / 2;

/** The vertical centre of the i-th node of a stack of `count` in `height`. */
const centreOf = (i: number, count: number, height: number) =>
  offsetOf(count, height) + i * STEP + NODE_H / 2;

const offsetStyle = (px: number) =>
  ({ '--stack-offset': `${px}px` }) as CSSProperties;

const Edges = ({
  entities,
  height,
  direction,
}: {
  entities: RelatedEntity[];
  height: number;
  direction: 'in' | 'out';
}) => {
  const classes = useStyles();
  const mid = height / 2;
  const count = entities.length;
  const ys = entities.map((_, i) => centreOf(i, count, height));
  return (
    <svg className={classes.gutter} width={GUTTER} height={height} aria-hidden>
      {entities.map((e, i) => {
        const [y1, y2] = direction === 'in' ? [ys[i], mid] : [mid, ys[i]];
        return (
          <path
            key={e.ref}
            d={`M0,${y1} C${GUTTER / 2},${y1} ${
              GUTTER / 2
            },${y2} ${GUTTER},${y2}`}
            fill="none"
            stroke="var(--ui-edge)"
            strokeWidth={1.5}
            strokeDasharray={e.resolved ? undefined : '4 3'}
          />
        );
      })}
      {/* The labels after every line, so none is painted over. Each sits
          beside its outer node, on the side its own curve bends away from. */}
      {entities.map((e, i) => (
        <text
          key={e.ref}
          className={classes.edgeLabel}
          x={direction === 'in' ? 4 : GUTTER - 4}
          y={ys[i] > mid ? ys[i] + 13 : ys[i] - 5}
          textAnchor={direction === 'in' ? 'start' : 'end'}
        >
          {relationOf(e)}
        </text>
      ))}
    </svg>
  );
};

const Node = ({ entity: e }: { entity: RelatedEntity }) => {
  const classes = useStyles();
  const entityRoute = useRouteRef(entityRouteRef);
  const name = e.title ?? e.name;
  const body = (
    <>
      <span className={`${classes.caption} ${classes.nodeCaption}`}>
        <span className={classes.kind}>{e.kind.toLowerCase()}</span>
        {!e.resolved && (
          <span className={classes.keep}>{'\u00a0·\u00a0unresolved'}</span>
        )}
        <span className={`${classes.relation} ${classes.kind}`}>
          {`\u00a0·\u00a0${relationOf(e)}`}
        </span>
      </span>
      <span className={classes.name}>{name}</span>
    </>
  );
  if (!e.resolved) {
    return (
      <div
        className={`${classes.node} ${classes.unresolved}`}
        data-unresolved="true"
        title={`${e.ref} is not in the catalog`}
      >
        {body}
      </div>
    );
  }
  const ref = parseEntityRef(e.ref);
  return (
    <Link
      className={classes.node}
      underline="none"
      title={`${name} · ${relationOf(e)}`}
      to={entityRoute({
        kind: ref.kind.toLowerCase(),
        namespace: ref.namespace.toLowerCase(),
        name: ref.name,
      })}
    >
      {body}
    </Link>
  );
};

const Side = ({
  label,
  entities,
  height,
  moreTo,
}: {
  label: string;
  entities: RelatedEntity[];
  height: number;
  moreTo: string;
}) => {
  const classes = useStyles();
  const shown = entities.slice(0, MAX_PER_SIDE);
  const hidden = entities.length - shown.length;
  return (
    <section role="group" aria-label={label} style={{ minWidth: 0 }}>
      <div className={classes.sideLabel}>{label}</div>
      <div
        className={classes.side}
        style={offsetStyle(offsetOf(shown.length, height))}
      >
        {shown.map(e => (
          <Node key={e.ref} entity={e} />
        ))}
        {!shown.length && (
          <Typography variant="body2" color="textSecondary">
            None declared
          </Typography>
        )}
        {hidden > 0 && (
          <Link to={moreTo} className={classes.more}>
            +{hidden} more on the Dependencies tab
          </Link>
        )}
      </div>
    </section>
  );
};

/**
 * The service topology (specs/component-page.md): who depends on the component
 * on the left, what it depends on on the right, direct relations only. The
 * relations are the ones the Dependencies tab lists and counts — one reading
 * for both. Owner and system are not dependencies and live in the ownership
 * card; the full graph with everything is one click away.
 */
export const Topology = () => {
  const classes = useStyles();
  const { entity, catalog, status } = useComponentPage();
  // Only what the status service measured: its stand-in state for a service
  // nothing checks is not a status.
  const health = status.data?.measured ? status.data.status : undefined;
  const entityRoute = useRouteRef(entityRouteRef);
  const base = entityRoute({
    kind: entity.kind.toLowerCase(),
    namespace: (entity.metadata.namespace ?? 'default').toLowerCase(),
    name: entity.metadata.name,
  });
  const expand = `/catalog-graph?rootEntityRefs%5B0%5D=${encodeURIComponent(
    stringifyEntityRef(entity),
  )}`;

  let content;
  if (catalog.loading) {
    content = <Progress />;
  } else if (catalog.error || !catalog.relations) {
    content = (
      <Typography role="alert">
        Couldn't load the dependencies from the catalog.
      </Typography>
    );
  } else {
    const { inbound, outbound } = catalog.relations;
    if (!inbound.length && !outbound.length) {
      content = (
        <Box>
          <Typography>No dependencies declared.</Typography>
          <Typography variant="body2" color="textSecondary">
            Declare what this component depends on with <code>dependsOn</code>{' '}
            in its <code>catalog-info.yaml</code>.
          </Typography>
        </Box>
      );
    } else {
      const nIn = Math.min(inbound.length, MAX_PER_SIDE);
      const nOut = Math.min(outbound.length, MAX_PER_SIDE);
      const height = heightFor(Math.max(nIn, nOut));
      content = (
        <div className={classes.graph}>
          <Side
            label="Depended on by"
            entities={inbound}
            height={height}
            moreTo={`${base}/dependencies`}
          />
          <Edges
            entities={inbound.slice(0, nIn)}
            height={height}
            direction="in"
          />
          <section
            role="group"
            aria-label="This component"
            style={{ minWidth: 0 }}
          >
            <div className={`${classes.sideLabel} ${classes.placeholder}`}>
              &nbsp;
            </div>
            <div
              className={classes.side}
              style={offsetStyle(offsetOf(1, height))}
            >
              <div className={`${classes.node} ${classes.centre}`}>
                {health && (
                  <span
                    role="img"
                    aria-label={`Status: ${STATUS_LABEL[health]}`}
                    title={`Status: ${STATUS_LABEL[health]}`}
                    className={classes.statusDot}
                    style={{ background: STATUS_COLOUR[health] }}
                  />
                )}
                <span className={classes.caption}>
                  component
                  {(entity.spec as { type?: string } | undefined)?.type
                    ? ` · ${(entity.spec as { type: string }).type}`
                    : ''}
                </span>
                <span className={classes.name}>
                  {entity.metadata.title ?? entity.metadata.name}
                </span>
              </div>
            </div>
          </section>
          <Edges
            entities={outbound.slice(0, nOut)}
            height={height}
            direction="out"
          />
          <Side
            label="Depends on"
            entities={outbound}
            height={height}
            moreTo={`${base}/dependencies`}
          />
        </div>
      );
    }
  }

  return (
    <InfoCard
      title="Service topology"
      subheader="Direct relations only"
      action={
        <Box pt={1} pr={1}>
          <Link to={expand}>Expand</Link>
        </Box>
      }
    >
      {content}
    </InfoCard>
  );
};
